<main id="main-content" class="h-screen flex flex-col pl-10 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Staff Skills Survey & Inventory
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip"
                    class="absolute z-30 invisible inline-block w-fit max-w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Assign the contacts listed in the <strong>Contacts</strong> list to positions in the
                            <strong>Staff Skills Survey & Inventory</strong> structure. Each position requires a primary and two alternates
                            to be assigned. The same person cannot be assigned to the primary role and as an alternate
                            to the same position.
                        </p>
                        <p>
                            To assign a person to a position, click on the position on the right side of the screen. It
                            will be highlighted orange when selected. Click on
                            <span class="inline-flex rounded-md shadow-sm gap-[2px] text-xs" role="group">
                                <span
                                    class="inline-flex p-2 rounded-s-md text-white bg-gradient-to-b from-nside-orange-100 to-nside-orange-200">
                                    Primary
                                </span>
                                <span
                                    class="inline-flex p-2 rounded-e-md text-white bg-gradient-to-b from-nside-gray-100 to-nside-gray-200">
                                    Alternate
                                </span>
                            </span> to assign the person to either the primary or alternate for this position.
                        </p>
                        <p>
                            To remove a person as the primary or alternate for a position, simply click on the person
                            you would like to remove from that position in the position assignment table.
                        </p>
                        <p>
                            To save the position assignment table, click on <span
                                class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span> when all necessary edits have been made. To clear all positions (does not delete),
                            click on <span
                                class="inline-flex w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center">
                                <lucide-icon [img]="icons.Eraser" size="20"></lucide-icon>
                            </span> . This will remove all persons that are currently assigned, but it will not delete
                            any assignments. To delete all assignments, click on
                            <span
                                class="inline-flex w-8 h-8 flex items-center justify-center text-white bg-gradient-to-b from-nside-gray-300 to-nside-gray-400 rounded">
                                <lucide-icon [img]="icons.Trash" size="20"></lucide-icon>
                            </span> . This will permanently delete any contact's assignment in the <strong>Staff Skills Survey & Inventory</strong> page.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant [sectionId]="sectionType"></eop-assistant>
        </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between gap-2">
                <h2 class="oswald-font text-3xl font-medium w-1/2">CONTACTS</h2>
                <div class="relative w-1/2">
                    <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                        <lucide-icon [name]="icons.SearchIcon" [size]="20"></lucide-icon>
                    </div>
                    <input type="search" [(ngModel)]="searchQuery" (input)="filterContacts()"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg ring-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent block w-full ps-10 p-2"
                        placeholder="Search" required />
                </div>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full mr-2 flex flex-col gap-2">
                    <ng-container *ngIf="contactsList.length > 0; else emptyContact">
                        <div *ngFor="let contact of contactsList" class="w-full flex flex-row gap-1">
                            <button
                                class="flex-grow flex items-center justify-start h-full p-2 border border-nside-gray-100 rounded-md bg-white text-start"
                                [ngClass]="{
                                    'text-white bg-gradient-to-b from-nside-orange-100 to-nside-orange-200 border-nside-orange-100 hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100': selectedContact === contact,
                                }" (click)="$event.preventDefault(); onContactChange(contact);">
                                <span class="text-start">{{ contact.name }}</span>
                            </button>
                        </div>
                    </ng-container>
                    <ng-template #emptyContact>
                        <div class="text-nside-gray-100 italic">
                            Empty list
                        </div>
                    </ng-template>
                </div>
            </div>
        </div>
        <form (ngSubmit)="saveStaffSkill()"
            class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="w-full flex items-center justify-between">
                <h2 class="w-full oswald-font text-3xl">STAFF SKILLS SURVEY:</h2>
                <div *ngIf="canSave()" class="w-full flex items-center justify-end gap-2">
                    <button data-tooltip-target="tooltip-request" data-tooltip-placement="bottom"
                        class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center hover:text-white hover:bg-nside-gray-200"
                        (click)="$event.preventDefault(); handleOpenModal('request-skills-survey');">
                        <lucide-icon [img]="icons.Mail" size="20"></lucide-icon>
                    </button>
                    <div id="tooltip-request" role="tooltip"
                        class="absolute z-30 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                        Request Digital Skills Survey
                        <div class="tooltip-arrow" data-popper-arrow></div>
                    </div>
                </div>
            </div>
            <div class="w-full h-full overflow-y-auto flex flex-grow items-start justify-center text-center gap-2">
                <div class="w-full h-full text-start overflow-y-auto p-2">
                    <label class="w-full font-medium">Please provide the relevant skills and capabilities you possess that would be helpful in case of emergency *</label>
                    <div *ngFor="let field of formFields" class="flex items-center mb-4">
                        <input type="checkbox" [(ngModel)]="formData[field]" [name]="field + '_' + field" [disabled]="!canSave()"
                            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 focus:ring-2">
                        <label for="default-checkbox" class="ms-2">{{ field }}</label>
                    </div>

                    <!-- Display dynamically added custom skills -->
                    <div *ngFor="let custom of customSkills" class="flex items-center mb-4">
                        <input type="checkbox" [(ngModel)]="formData[custom]" [name]="custom + '_' + custom" [disabled]="!canSave()"
                            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 focus:ring-2" />
                        <label class="ms-2">{{ custom }}</label>
                    </div>

                    <div *ngIf="formData['Other'] && canSave()" class="flex items-center gap-2">
                        <input type="text" [(ngModel)]="newCustomSkill" name="newCustomSkill"
                            placeholder="Enter a custom skill..."
                            class="flex-1 border border-gray-300 rounded-md p-2 text-sm" />
                        <button type="button"
                            class="px-3 py-2 rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 text-white text-sm oswald-font hover:from-nside-orange-100 hover:to-nside-orange-200"
                            (click)="addCustomSkill()">
                            Add Skill
                        </button>
                    </div>

                    <!-- COMMENTS -->
                    <div class="w-full mt-4 text-start">
                        <label class="w-full font-medium">Please list any equipment or supplies that you have available. This should include an evaluation of equipment and supplies uniquely for individuals with disabilities, such as evacuation chairs, the availability of sign language interpreters and technology used for effective communication, accessible transportation, and consumable medical supplies and durable medical equipment that may be necessary during a shelter-in-place or evacuation.</label>
                        <textarea [(ngModel)]="formData['comments']" rows="4" [name]="'comments'" [disabled]="!canSave()"
                            class="w-full text-sm border rounded-md p-2 mt-1"></textarea>
                    </div>
                </div>
            </div>
            <div class="flex items-center justify-end gap-2 oswald-font">
                <button 
                    *ngIf="canSave()" 
                    type="submit"
                    class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                    [ngClass]="{
                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canSave(),
                        'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': canSave()
                    }" 
                    [disabled]="!canSave()" 
                >
                    Save
                </button>
            </div>
        </form>
    </div>

    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <send-al-assessment *ngIf="showModalType === 'request-skills-survey'" [assessType]="'AL-Skills Survey & Inventory'"
        (onClose)="handleCloseModal('request-skills-survey')"
        (onSave)="handleSave($event, 'request-skills-survey')"></send-al-assessment>

    <!-- <delete-confirmation
        *ngIf="deleteConfirm"
        [text]="'all records'"
        [itemToDelete]="currentBuildingId"
        (onClose)="cancelDelete()"
        (onDelete)="deleteAllRecords($event)"
    ></delete-confirmation> -->

    <introduction></introduction>

    <prv-next-buttons></prv-next-buttons>

    <popup-message [type]="messageModal" [message]="popupMessage" (close)="resetMessageModal()"></popup-message>
</main>